/**
 * PersistRunHistory — the per-PDO job/run log.
 *
 * One row per materialization (`GET /tenant/pdos/:id/materializations`), showing
 * what the user actually needs to reason about a run: when it started, the
 * watermark window it consumed, how long it took, and the row outcome
 * (read / inserted / updated / ignored / closed). Distinct from the watermark
 * *audit* (mode-flips / advances) in WatermarkStateSection — this is the job log.
 *
 * Shared by the persist node inspector and the PDO detail page so the run log
 * reads the same everywhere. All timestamps are absolute (standing rule).
 *
 * 0-impact runs (ADR-0103) carry `summary.no_change` + a null `pdo_version_id`;
 * they render with a "no change" tag and DO appear here (the version is
 * unchanged but the run happened and may have advanced the watermark).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { apiFetch } from "../../lib/api.js";
import { ErrorState } from "../States.js";

type WriteMode = "replace_snapshot" | "append" | "merge" | "merge_history";

type MaterializationRow = {
  id: string;
  status: "running" | "success" | "failed";
  trigger_type: "manual" | "api" | "schedule";
  started_at: string;
  completed_at: string | null;
  duration_ms: number | null;
  row_count: number | null;
  pdo_version_id: string | null;
  error_message: string | null;
  reinitialize_reason: string | null;
  watermark_mode_at_start: "initialize" | "increment" | null;
  watermark_value_at_start: string | null;
  watermark_value_at_end: string | null;
  watermark_advance_error: string | null;
  summary: Record<string, unknown> | null;
};

type RejectRow = {
  id: string;
  error_type: string;
  message: string;
  created_at: string;
  row_data: Record<string, unknown>;
};

export type PersistRunHistoryProps = {
  pdoId: string;
  /** Drives the Closed column visibility (always shown for merge_history). */
  writeMode?: WriteMode;
  /** The current version number, for the Last Execution summary label. */
  currentVersionNumber?: number | null;
  /** Optional version list to resolve a per-row version number (no UUIDs shown). */
  versions?: Array<{ id: string; version_number: number; created_at?: string }>;
  /** Bump to force a re-fetch (e.g. after a run completes). */
  refreshNonce?: number;
  /** Max rows shown in the table (default 25). */
  limit?: number;
  /**
   * When true, the Run History table collapses behind a disclosure (default
   * collapsed) — used in the narrow node inspector. Last Execution stays
   * always-visible. PdoDetail leaves it expanded (full-page room).
   */
  collapsible?: boolean;
  /**
   * Called whenever the latest run's `running` state changes. Lets a parent
   * (the persist inspector) reconcile its run controls with server truth — the
   * local "executing" flag resets on remount, but a run started before the user
   * navigated away is still in flight. While active, this component polls so the
   * caller (and the table) learn when the server finishes.
   */
  onActiveChange?: (active: boolean) => void;
  /**
   * Set true the instant the parent kicks off a run locally, before the server's
   * `running` row has been fetched. Forces polling to start immediately so the
   * run-log block shows `running` without waiting for the next mount — otherwise
   * polling can't begin until a `running` row is already visible (chicken/egg).
   */
  externallyActive?: boolean;
};

/** How often to re-poll the run log while a run is in flight. */
const RUN_POLL_MS = 2500;

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

function fmtTs(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    year: "numeric", month: "short", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
}
function fmtMs(n: number | null): string {
  if (n == null) return "—";
  if (n < 1000) return `${n}ms`;
  return `${(n / 1000).toFixed(2)}s`;
}
function fmtRows(n: number | null): string {
  return n == null ? "—" : n.toLocaleString();
}
function num(v: unknown): number | null {
  return typeof v === "number" ? v : null;
}
function statusPill(s: "running" | "success" | "failed"): string {
  return s === "success" ? "pillOk" : s === "failed" ? "pillBad" : "pillWarn";
}

/**
 * Per-run row breakdown from a materialization's `summary` JSONB.
 *  read    = rows_read (provider output count)
 *  ins     = rows_new
 *  upd     = rows_changed
 *  ign     = rows_unchanged + rows_skipped (append-mode dupes / identical merges)
 *  closed  = rows_closed (merge_history SCD2 closes)
 */
function runRowStats(summary: Record<string, unknown> | null): {
  read: number | null; inserted: number | null; updated: number | null;
  ignored: number | null; closed: number | null; noChange: boolean;
} {
  if (!summary) return { read: null, inserted: null, updated: null, ignored: null, closed: null, noChange: false };
  const unchanged = num(summary.rows_unchanged);
  const skipped = num(summary.rows_skipped);
  return {
    read: num(summary.rows_read),
    inserted: num(summary.rows_new),
    updated: num(summary.rows_changed),
    ignored: unchanged == null && skipped == null ? null : (unchanged ?? 0) + (skipped ?? 0),
    closed: num(summary.rows_closed),
    noChange: summary.no_change === true,
  };
}

/** ADR-0103: a rollback run carries summary.action='rollback' + restored_from_version. */
function isRollbackRun(summary: Record<string, unknown> | null): boolean {
  return summary?.action === "rollback";
}

/**
 * A run that failed in the upstream pull (e.g. an ODBC timeout) carries the
 * fully-rendered SQL that ran in `summary.failure.upstream_sql` — surfaced so the
 * run can be debugged without reaching into the database. Absent for failures
 * that have no upstream statement (missing upstream data, merge-stage errors).
 */
function failingSql(summary: Record<string, unknown> | null): { sql: string; node: string | null } | null {
  const f = summary?.failure as Record<string, unknown> | undefined;
  if (f && typeof f.upstream_sql === "string" && f.upstream_sql.length > 0) {
    return { sql: f.upstream_sql, node: typeof f.failing_node === "string" ? f.failing_node : null };
  }
  return null;
}

/** Rendered SQL of a failed run, in a scrollable monospace block. */
function FailingSqlBlock({ sql, node }: { sql: string; node: string | null }) {
  return (
    <div style={{ display: "grid", gap: 4 }}>
      {node ? (
        <div className="muted" style={{ fontSize: 11 }}>
          Failing provider: <span className="mono">{node}</span> — the statement below is exactly what ran (variables substituted).
        </div>
      ) : null}
      <pre
        className="mono"
        style={{
          margin: 0, fontSize: 11, lineHeight: 1.4, whiteSpace: "pre",
          overflow: "auto", maxHeight: 320,
          border: "1px solid var(--border)", borderRadius: 6, padding: "8px 10px",
          background: "var(--surface, #fff)",
        }}
      >
        {sql}
      </pre>
    </div>
  );
}

/** The watermark window a run consumed → advanced to (null when not bound). */
function watermarkCell(m: MaterializationRow): string | null {
  const hasInfo = m.watermark_value_at_start != null || m.watermark_value_at_end != null || m.watermark_mode_at_start != null;
  if (!hasInfo) return null;
  const start = m.watermark_mode_at_start === "initialize" ? "init" : (m.watermark_value_at_start ?? "—");
  const end = m.watermark_value_at_end ?? "—";
  return start === end ? start : `${start} → ${end}`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function PersistRunHistory(props: PersistRunHistoryProps) {
  const { pdoId, writeMode, currentVersionNumber, versions, refreshNonce, limit = 25, collapsible = false } = props;

  const [mats, setMats] = useState<MaterializationRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [expandedErrors, setExpandedErrors] = useState<RejectRow[]>([]);
  const [sqlExpanded, setSqlExpanded] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch<{ materializations: MaterializationRow[] }>(
        `/api/tenant/pdos/${encodeURIComponent(pdoId)}/materializations`,
      );
      setMats(data.materializations);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [pdoId]);

  useEffect(() => { void load(); }, [load, refreshNonce]);

  // Quiet re-poll: refresh rows without toggling the loading spinner or
  // clobbering existing data on a transient error (used while a run is active).
  const quietReload = useCallback(async () => {
    try {
      const data = await apiFetch<{ materializations: MaterializationRow[] }>(
        `/api/tenant/pdos/${encodeURIComponent(pdoId)}/materializations`,
      );
      setMats(data.materializations);
    } catch {
      /* keep prior data on a transient poll error */
    }
  }, [pdoId]);

  // The latest run is the source of truth for "is a run in flight".
  const runActive = (mats?.[0]?.status ?? null) === "running";

  // Report active-state changes up. A ref avoids forcing the parent to memoize.
  const onActiveChangeRef = useRef(props.onActiveChange);
  onActiveChangeRef.current = props.onActiveChange;
  useEffect(() => { onActiveChangeRef.current?.(runActive); }, [runActive]);

  // Poll while a run is in flight — either one we can already see (`runActive`)
  // or one the parent just started locally before its `running` row is visible
  // (`externallyActive`). The latter breaks the chicken/egg where polling could
  // never start until a `running` row was already on screen. Refetch immediately
  // so the run-log block flips to `running` without waiting a full tick, and
  // keep polling so the table learns when the server finishes (the triggering
  // request may be on a component instance that has since unmounted).
  const shouldPoll = runActive || !!props.externallyActive;
  useEffect(() => {
    if (!shouldPoll) return;
    void quietReload();
    const t = setInterval(() => { void quietReload(); }, RUN_POLL_MS);
    return () => clearInterval(t);
  }, [shouldPoll, quietReload]);

  const toggleErrors = useCallback(async (matId: string) => {
    if (expanded === matId) { setExpanded(null); return; }
    setExpanded(matId);
    setExpandedErrors([]);
    try {
      const data = await apiFetch<{ errors: RejectRow[] }>(
        `/api/tenant/pdos/${encodeURIComponent(pdoId)}/errors?materialization_id=${encodeURIComponent(matId)}`,
      );
      setExpandedErrors(data.errors);
    } catch {
      setExpandedErrors([]);
    }
  }, [expanded, pdoId]);

  if (error) return <ErrorState detail={error} />;
  if (loading && mats === null) {
    return <div className="muted" style={{ fontSize: 12 }}>Loading run history…</div>;
  }
  if (!mats || mats.length === 0) {
    return <div className="muted" style={{ fontSize: 12 }}>No runs yet.</div>;
  }

  const rows = mats.slice(0, limit);
  const showWatermark = mats.some((m) => watermarkCell(m) !== null);
  const showClosed = writeMode === "merge_history" || mats.some((m) => (num(m.summary?.rows_closed) ?? 0) > 0);
  const showVersionCol = Array.isArray(versions);
  const versionLabel = (m: MaterializationRow): string => {
    // A run that produced a version links to it directly.
    if (m.pdo_version_id != null) {
      const v = versions?.find((vv) => vv.id === m.pdo_version_id);
      return v ? `v${v.version_number}` : "—";
    }
    // No version produced (e.g. a no-change run). It left the store at whichever
    // version was current then — the latest version created at or before this
    // run. The "no change" qualifier lives in the Note column, not here.
    if (m.status === "success" && versions && versions.length) {
      const startedMs = new Date(m.started_at).getTime();
      let best: { version_number: number; ms: number } | null = null;
      for (const v of versions) {
        if (v.created_at == null) continue;
        const ms = new Date(v.created_at).getTime();
        if (ms <= startedMs && (best == null || ms > best.ms)) {
          best = { version_number: v.version_number, ms };
        }
      }
      if (best) return `v${best.version_number}`;
    }
    return "—";
  };
  // Column count for the error-detail colspan.
  // Fixed: Started, Trigger, Status, Note, Exec, Read, Ins, Upd, Ign, Errors = 10.
  const colCount = 10 + (showWatermark ? 1 : 0) + (showClosed ? 1 : 0) + (showVersionCol ? 1 : 0);

  const latest = mats[0];
  const ls = runRowStats(latest.summary);

  const runHistoryTable = (
    <>
      <div className="tableWrap">
        <table className="table tableCompact">
          <thead>
            <tr>
              <th>Started</th>
              <th>Trigger</th>
              <th>Status</th>
              <th title="Run qualifiers: rollback, no change, etc.">Note</th>
              {showVersionCol ? <th>Version</th> : null}
              {showWatermark ? <th title="Watermark window this run consumed → advanced to">Watermark</th> : null}
              <th style={{ textAlign: "right" }}>Exec</th>
              <th style={{ textAlign: "right" }} title="Rows the provider sent into this run">Read</th>
              <th style={{ textAlign: "right" }} title="New keys written">Ins</th>
              <th style={{ textAlign: "right" }} title="Existing keys whose tracked values changed">Upd</th>
              <th style={{ textAlign: "right" }} title="Unchanged + skipped duplicates">Ign</th>
              {showClosed ? <th style={{ textAlign: "right" }} title="Keys removed from source and tombstoned (merge_history, close-on-missing)">Del</th> : null}
              <th style={{ textAlign: "right" }}>Errors</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((m) => {
              const s = runRowStats(m.summary);
              const rejects = num(m.summary?.rejects_total) ?? 0;
              const wm = watermarkCell(m);
              return (
                <>
                  <tr key={m.id}>
                    <td style={{ fontSize: 12 }} title={fmtTs(m.started_at)}>{fmtTs(m.started_at)}</td>
                    <td style={{ fontSize: 12 }}>{m.trigger_type}</td>
                    <td><span className={statusPill(m.status)}>{m.status}</span></td>
                    <td style={{ fontSize: 11 }}>
                      {(() => {
                        const notes: string[] = [];
                        // Rollback is shown as "↩ vN" in the Version column when
                        // it's present (PdoDetail). Without that column (inspector),
                        // the Note carries it.
                        if (isRollbackRun(m.summary) && !showVersionCol) notes.push("rollback");
                        if (s.noChange) notes.push("no change");
                        const fs = m.status === "failed" ? failingSql(m.summary) : null;
                        return (
                          <span style={{ display: "inline-flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                            <span className="muted">{notes.length ? notes.join(" · ") : "—"}</span>
                            {fs ? (
                              <button
                                type="button"
                                className="linkBtn"
                                style={{ fontSize: 11 }}
                                onClick={() => setSqlExpanded(sqlExpanded === m.id ? null : m.id)}
                                aria-expanded={sqlExpanded === m.id}
                              >
                                {sqlExpanded === m.id ? "Hide SQL" : "View SQL"}
                              </button>
                            ) : null}
                          </span>
                        );
                      })()}
                    </td>
                    {showVersionCol ? (
                      <td className="mono" style={{ fontSize: 12 }}>
                        {versionLabel(m)}
                        {(() => {
                          const restoredFrom = num(m.summary?.restored_from_version);
                          return restoredFrom != null ? (
                            <span className="muted" style={{ marginLeft: 6, fontSize: 11 }} title={`Rolled back from v${restoredFrom}`}>↩ v{restoredFrom}</span>
                          ) : null;
                        })()}
                      </td>
                    ) : null}
                    {showWatermark ? <td className="mono" style={{ fontSize: 11 }} title={wm ?? undefined}>{wm ?? "—"}</td> : null}
                    <td style={{ fontSize: 12, textAlign: "right" }}>{fmtMs(m.duration_ms)}</td>
                    <td className="mono" style={{ fontSize: 12, textAlign: "right" }}>{fmtRows(s.read)}</td>
                    <td className="mono" style={{ fontSize: 12, textAlign: "right" }}>{fmtRows(s.inserted)}</td>
                    <td className="mono" style={{ fontSize: 12, textAlign: "right" }}>{fmtRows(s.updated)}</td>
                    <td className="mono" style={{ fontSize: 12, textAlign: "right" }}>{fmtRows(s.ignored)}</td>
                    {showClosed ? <td className="mono" style={{ fontSize: 12, textAlign: "right" }}>{fmtRows(s.closed)}</td> : null}
                    <td style={{ textAlign: "right" }}>
                      {rejects > 0 ? (
                        <button
                          className="pillWarn"
                          type="button"
                          style={{ cursor: "pointer", border: "none", background: "none", padding: 0 }}
                          onClick={() => void toggleErrors(m.id)}
                          aria-expanded={expanded === m.id}
                        >
                          {rejects} error{rejects !== 1 ? "s" : ""}
                        </button>
                      ) : (
                        <span className="muted" style={{ fontSize: 12 }}>0</span>
                      )}
                    </td>
                  </tr>
                  {sqlExpanded === m.id ? (
                    <tr key={`${m.id}-sql`}>
                      <td colSpan={colCount} style={{ padding: "8px 12px", background: "var(--bg-subtle, #f8f8f8)" }}>
                        {(() => {
                          const fs = failingSql(m.summary);
                          return fs ? <FailingSqlBlock sql={fs.sql} node={fs.node} /> : null;
                        })()}
                      </td>
                    </tr>
                  ) : null}
                  {expanded === m.id ? (
                    <tr key={`${m.id}-errors`}>
                      <td colSpan={colCount} style={{ padding: "8px 12px", background: "var(--bg-subtle, #f8f8f8)" }}>
                        {(() => {
                          const total = num(m.summary?.rejects_total) ?? 0;
                          const sampled = num(m.summary?.rejects_sampled) ?? expandedErrors.length;
                          const overflow = total > 0 && total > sampled;
                          return (
                            <>
                              {total > 0 ? (
                                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>
                                  {total} row{total !== 1 ? "s" : ""} rejected
                                  {overflow ? (
                                    <span className="muted" style={{ fontWeight: 400, marginLeft: 6 }}>
                                      (sample of first {sampled} shown)
                                    </span>
                                  ) : null}
                                </div>
                              ) : null}
                              {expandedErrors.length === 0 ? (
                                <span className="muted">No error detail loaded.</span>
                              ) : (
                                <div style={{ display: "grid", gap: 8 }}>
                                  {expandedErrors.map((err) => <RejectRowDetail key={err.id} err={err} />)}
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </td>
                    </tr>
                  ) : null}
                </>
              );
            })}
          </tbody>
        </table>
      </div>
      {mats.length > rows.length ? (
        <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
          Showing the {rows.length} most recent of {mats.length} runs.
        </div>
      ) : null}
    </>
  );

  return (
    // minmax(0, 1fr): keep this component (and its wide Run History table) sized
    // to the frame so the table scrolls within its own tableWrap rather than
    // widening the inspector panel.
    <div style={{ display: "grid", gap: 10, gridTemplateColumns: "minmax(0, 1fr)" }}>
      {/* Last Execution — persistent (survives navigation; reflects 0-impact runs). */}
      <div className="field">
        <label className="label">Last Execution</label>
        <div style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13, flexWrap: "wrap" }}>
          <span className={statusPill(latest.status)}>{latest.status}</span>
          {isRollbackRun(latest.summary) ? (
            <span className="pill" title="Restored a prior rollback point to current">rollback</span>
          ) : null}
          {ls.noChange ? (
            <span className="pill" title="0-impact run — no new version written">no change</span>
          ) : null}
          {latest.status === "success" && currentVersionNumber != null ? (
            <span className="mono" style={{ fontSize: 12 }}>v{currentVersionNumber}</span>
          ) : null}
          {latest.row_count != null ? (
            <span className="muted" style={{ fontSize: 12 }}>{latest.row_count.toLocaleString()} rows</span>
          ) : null}
          <span className="muted" style={{ fontSize: 12 }}>{fmtMs(latest.duration_ms)}</span>
          <span className="muted" style={{ fontSize: 12 }}>{fmtTs(latest.started_at)}</span>
        </div>
        {latest.status === "success" && (ls.read != null || ls.inserted != null || ls.updated != null || ls.ignored != null) ? (
          <div style={{ display: "flex", gap: 14, marginTop: 6, fontSize: 12, flexWrap: "wrap" }}>
            <span title="Rows the provider sent into this run"><span className="muted">Read</span> <span className="mono">{fmtRows(ls.read)}</span></span>
            <span title="New keys written"><span className="muted">Inserted</span> <span className="mono">{fmtRows(ls.inserted)}</span></span>
            <span title="Existing keys whose tracked values changed"><span className="muted">Updated</span> <span className="mono">{fmtRows(ls.updated)}</span></span>
            <span title="Unchanged + skipped duplicates"><span className="muted">Ignored</span> <span className="mono">{fmtRows(ls.ignored)}</span></span>
            {showClosed ? (
              <span title="Keys removed from source and tombstoned (merge_history, close-on-missing)"><span className="muted">Deleted</span> <span className="mono">{fmtRows(ls.closed)}</span></span>
            ) : null}
          </div>
        ) : null}
        {latest.status === "failed" && latest.error_message ? (
          <div className="help" style={{ color: "var(--danger-text)" }}>{latest.error_message}</div>
        ) : null}
        {latest.status === "failed" && failingSql(latest.summary) ? (
          <details style={{ marginTop: 6 }}>
            <summary style={{ cursor: "pointer", fontSize: 12, fontWeight: 600, color: "var(--muted)", userSelect: "none" }}>
              Failing SQL
            </summary>
            <div style={{ marginTop: 6 }}>
              {(() => {
                const fs = failingSql(latest.summary)!;
                return <FailingSqlBlock sql={fs.sql} node={fs.node} />;
              })()}
            </div>
          </details>
        ) : null}
      </div>

      {/* Run History — the job log. Collapsible in the narrow node inspector. */}
      {collapsible ? (
        <details>
          <summary style={{ cursor: "pointer", fontSize: 12, fontWeight: 600, color: "var(--muted)", userSelect: "none" }}>
            Run History ({mats.length})
          </summary>
          <div style={{ marginTop: 8 }}>{runHistoryTable}</div>
        </details>
      ) : (
        <div className="field">
          <label className="label">Run History</label>
          {runHistoryTable}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Reject-row detail (moved here from PdoDetail so both surfaces share it)
// ---------------------------------------------------------------------------

export function RejectRowDetail({ err }: { err: RejectRow }) {
  const entries = Object.entries(err.row_data ?? {});
  return (
    <div style={{ border: "1px solid var(--border)", borderRadius: 6, padding: "8px 10px", background: "var(--surface, #fff)", fontSize: 12 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 4 }}>
        <span className="pill pillWarn">{err.error_type}</span>
        <span className="muted" style={{ fontSize: 11 }}>{fmtTs(err.created_at)}</span>
      </div>
      <div style={{ marginBottom: 6 }}>{err.message}</div>
      {entries.length > 0 ? (
        <div style={{ display: "grid", gridTemplateColumns: "max-content 1fr", columnGap: 12, rowGap: 2, fontSize: 11, fontFamily: "var(--font-mono, monospace)" }}>
          {entries.map(([k, v]) => (
            <>
              <div key={`k-${k}`} className="muted">{k}</div>
              <div key={`v-${k}`} style={{ wordBreak: "break-word" }}>
                {v === null || v === undefined ? <span className="muted">null</span> : String(v)}
              </div>
            </>
          ))}
        </div>
      ) : null}
    </div>
  );
}
